import { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileSpreadsheet,
  Upload,
  Sparkles,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  File,
  X,
  Search,
  Brain,
  Pencil,
  Check,
  Download,
} from 'lucide-react';
import clsx from 'clsx';
import { accountsApi } from '@/api/client';
import { useAppStore } from '@/store';
import type { EntityAccount, GroupAccount } from '@/types';

// --- Types ------------------------------------------------------------------

interface COASummary {
  entity_id: number;
  entity_code: string;
  entity_name: string;
  functional_currency: string;
  total_accounts: number;
  mapped_accounts: number;
  unmapped_accounts: number;
  mapping_pct: number;
}

// --- Entity COA Page --------------------------------------------------------

export default function EntityCOA() {
  const { addNotification } = useAppStore();

  const [summaries, setSummaries] = useState<COASummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Expanded entity detail
  const [expandedEntity, setExpandedEntity] = useState<number | null>(null);
  const [entityAccounts, setEntityAccounts] = useState<EntityAccount[]>([]);
  const [entityAccountsLoading, setEntityAccountsLoading] = useState(false);

  // Upload modal
  const [uploadEntityId, setUploadEntityId] = useState<number | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    rows_processed: number; created: number; updated: number; skipped: number;
    warnings: string[]; errors: string[];
    ai_column_detection?: boolean; detected_code_column?: string; detected_name_column?: string;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bulk mapping upload
  const [showBulkMapping, setShowBulkMapping] = useState(false);
  const [bulkMappingFile, setBulkMappingFile] = useState<File | null>(null);
  const [bulkMappingUploading, setBulkMappingUploading] = useState(false);
  const [bulkMappingResult, setBulkMappingResult] = useState<{
    total_mapped: number; total_skipped: number; entities_processed: number;
    entity_results: { entity_code: string; entity_name: string; sheet: string | null; explicit_mapped: number; wildcard_mapped: number; skipped: number; status: string }[];
    warnings: string[];
  } | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  // AI Mapping
  const [aiMappingEntityId, setAiMappingEntityId] = useState<number | null>(null);

  // Account search filter
  const [accountSearch, setAccountSearch] = useState('');

  // Inline edit mapping
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [groupAccounts, setGroupAccounts] = useState<GroupAccount[]>([]);
  const [groupAccountsLoaded, setGroupAccountsLoaded] = useState(false);
  const [gaSearchQuery, setGaSearchQuery] = useState('');
  const [savingMapping, setSavingMapping] = useState(false);
  const gaSearchRef = useRef<HTMLInputElement>(null);

  // --- Load data ---
  const loadSummaries = useCallback(async () => {
    try {
      const data = await accountsApi.coaSummary();
      setSummaries(data);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load entity COA data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummaries();
  }, [loadSummaries]);

  // Load group accounts once when first edit is triggered
  const ensureGroupAccountsLoaded = useCallback(async () => {
    if (groupAccountsLoaded) return;
    try {
      const data = await accountsApi.listGroup();
      setGroupAccounts(data);
      setGroupAccountsLoaded(true);
    } catch {
      // silent
    }
  }, [groupAccountsLoaded]);

  // --- Expand / collapse entity ---
  const toggleEntity = async (entityId: number) => {
    if (expandedEntity === entityId) {
      setExpandedEntity(null);
      setEntityAccounts([]);
      setEditingAccountId(null);
      setAccountSearch('');
      return;
    }
    setExpandedEntity(entityId);
    setEntityAccountsLoading(true);
    setEditingAccountId(null);
    setAccountSearch('');
    try {
      const res = await accountsApi.listEntity(String(entityId));
      const accounts = Array.isArray(res) ? res : (res as any).items ?? res;
      setEntityAccounts(accounts);
    } catch {
      setEntityAccounts([]);
    } finally {
      setEntityAccountsLoading(false);
    }
  };

  // --- Upload COA ---
  const handleUpload = async () => {
    if (!uploadFile || !uploadEntityId) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await accountsApi.uploadCOA(String(uploadEntityId), uploadFile);
      setUploadResult(result);
      if (result.errors.length === 0) {
        addNotification({
          type: 'success',
          title: 'COA Uploaded',
          message: `${result.created} created, ${result.updated} updated.${result.ai_column_detection ? ` AI detected columns: ${result.detected_code_column} → code, ${result.detected_name_column} → name.` : ''}`,
        });
      }
      await loadSummaries();
      if (expandedEntity === uploadEntityId) {
        const res = await accountsApi.listEntity(String(uploadEntityId));
        const accounts = Array.isArray(res) ? res : (res as any).items ?? res;
        setEntityAccounts(accounts);
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      const errMsg = apiErr.detail || 'Upload failed.';
      setUploadResult({ rows_processed: 0, created: 0, updated: 0, skipped: 0, warnings: [], errors: [errMsg] });
      addNotification({ type: 'error', title: 'Upload Failed', message: errMsg });
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setUploadEntityId(null);
    setUploadFile(null);
    setUploadResult(null);
  };

  // --- Bulk Mapping Upload ---
  const handleBulkMappingUpload = async () => {
    if (!bulkMappingFile) return;
    setBulkMappingUploading(true);
    setBulkMappingResult(null);
    try {
      const result = await accountsApi.uploadBulkMapping(bulkMappingFile);
      setBulkMappingResult(result);
      addNotification({
        type: result.total_mapped > 0 ? 'success' : 'warning',
        title: 'Bulk Mapping Complete',
        message: `${result.total_mapped} mapped across ${result.entities_processed} entities. ${result.total_skipped} skipped.`,
      });
      await loadSummaries();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      const errMsg = apiErr.detail || 'Bulk mapping upload failed.';
      addNotification({ type: 'error', title: 'Upload Failed', message: errMsg });
    } finally {
      setBulkMappingUploading(false);
    }
  };

  const closeBulkMappingModal = () => {
    setShowBulkMapping(false);
    setBulkMappingFile(null);
    setBulkMappingResult(null);
  };

  // --- AI Map ---
  const handleAiMap = async (entityId: number) => {
    setAiMappingEntityId(entityId);
    try {
      const result = await accountsApi.aiMap(String(entityId));
      addNotification({
        type: result.auto_mapped > 0 ? 'success' : 'warning',
        title: 'AI Mapping Complete',
        message: `${result.auto_mapped} accounts mapped, ${result.skipped} skipped.${result.errors.length > 0 ? ` ${result.errors.length} error(s).` : ''}`,
      });
      await loadSummaries();
      if (expandedEntity === entityId) {
        const res = await accountsApi.listEntity(String(entityId));
        const accounts = Array.isArray(res) ? res : (res as any).items ?? res;
        setEntityAccounts(accounts);
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'AI Mapping Failed', message: apiErr.detail || 'AI mapping failed.' });
    } finally {
      setAiMappingEntityId(null);
    }
  };

  // --- Edit mapping ---
  const startEditing = async (accountId: string) => {
    await ensureGroupAccountsLoaded();
    setEditingAccountId(accountId);
    setGaSearchQuery('');
    setTimeout(() => gaSearchRef.current?.focus(), 100);
  };

  const cancelEditing = () => {
    setEditingAccountId(null);
    setGaSearchQuery('');
  };

  const saveMapping = async (entityAccountId: string, groupAccountId: string) => {
    setSavingMapping(true);
    try {
      await accountsApi.mapAccount(entityAccountId, groupAccountId);
      // Update local state immediately
      const ga = groupAccounts.find((g) => String(g.id) === groupAccountId);
      setEntityAccounts((prev) =>
        prev.map((ea) =>
          String(ea.id) === entityAccountId
            ? {
                ...ea,
                group_account_id: groupAccountId,
                group_account_code: ga?.code,
                group_account_name: ga?.name,
                mapping_status: 'mapped',
                is_mapped: true,
              }
            : ea
        )
      );
      setEditingAccountId(null);
      setGaSearchQuery('');
      addNotification({ type: 'success', title: 'Mapping Updated', message: `Mapped to ${ga?.code} — ${ga?.name}` });
      // Refresh summaries in background
      loadSummaries();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Mapping Failed', message: apiErr.detail || 'Failed to update mapping.' });
    } finally {
      setSavingMapping(false);
    }
  };

  // Filtered group accounts for search
  const filteredGroupAccounts = gaSearchQuery
    ? groupAccounts.filter(
        (ga) =>
          ga.code.toLowerCase().includes(gaSearchQuery.toLowerCase()) ||
          ga.name.toLowerCase().includes(gaSearchQuery.toLowerCase())
      )
    : groupAccounts;

  // --- Filter ---
  const filtered = summaries.filter((s) =>
    !searchQuery ||
    s.entity_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.entity_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAccounts = summaries.reduce((a, s) => a + s.total_accounts, 0);
  const totalMapped = summaries.reduce((a, s) => a + s.mapped_accounts, 0);

  // --- Loading ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton w-64 h-7" />
        <div className="skeleton w-full h-16 rounded-xl" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton w-full h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const uploadEntity = summaries.find((s) => s.entity_id === uploadEntityId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Entity Chart of Accounts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload, view, and AI-map entity-level chart of accounts to group accounts.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => { setShowBulkMapping(true); setBulkMappingFile(null); setBulkMappingResult(null); }}
            className="btn-primary text-xs px-3 py-2"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Bulk Upload Mapping
          </button>
          <button
            onClick={() => accountsApi.downloadCoaTemplate()}
            className="btn-secondary text-xs px-3 py-2"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Sample COA Template
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase">Entities</p>
          <p className="text-lg font-bold text-gray-900">{summaries.length}</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase">Total Accounts</p>
          <p className="text-lg font-bold text-gray-900">{totalAccounts.toLocaleString()}</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase">Mapped</p>
          <p className="text-lg font-bold text-green-600">{totalMapped.toLocaleString()}</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase">Unmapped</p>
          <p className="text-lg font-bold text-amber-600">{(totalAccounts - totalMapped).toLocaleString()}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search entities by name or code..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Entity rows */}
      <div className="space-y-2">
        {filtered.map((s) => (
          <div key={s.entity_id} className="card overflow-hidden">
            {/* Entity row */}
            <div
              className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleEntity(s.entity_id)}
            >
              <button className="p-0.5">
                {expandedEntity === s.entity_id ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-400">{s.entity_code}</span>
                  <span className="text-sm font-semibold text-gray-900 truncate">{s.entity_name}</span>
                  <span className="badge bg-gray-100 text-gray-600 text-xs">{s.functional_currency}</span>
                </div>
              </div>

              {/* Counts */}
              <div className="flex items-center gap-6 text-xs">
                <div className="text-center">
                  <p className="text-gray-400 uppercase">Accounts</p>
                  <p className="font-bold text-gray-900">{s.total_accounts}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 uppercase">Mapped</p>
                  <p className="font-bold text-green-600">{s.mapped_accounts}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 uppercase">Unmapped</p>
                  <p className="font-bold text-amber-600">{s.unmapped_accounts}</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-28">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all',
                        s.mapping_pct === 100 ? 'bg-green-500' : s.mapping_pct > 50 ? 'bg-primary-500' : 'bg-amber-400',
                      )}
                      style={{ width: `${s.mapping_pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-600 w-10 text-right">{s.mapping_pct}%</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => { setUploadEntityId(s.entity_id); setUploadResult(null); setUploadFile(null); }}
                  className="btn-secondary text-xs px-3 py-1.5"
                >
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload COA
                </button>
                <button
                  onClick={() => handleAiMap(s.entity_id)}
                  disabled={aiMappingEntityId === s.entity_id || s.unmapped_accounts === 0}
                  className="btn-primary text-xs px-3 py-1.5"
                >
                  {aiMappingEntityId === s.entity_id ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Mapping...</>
                  ) : (
                    <><Brain className="w-3.5 h-3.5 mr-1" /> AI Map</>
                  )}
                </button>
              </div>
            </div>

            {/* Expanded detail */}
            {expandedEntity === s.entity_id && (
              <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                {entityAccountsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading accounts...
                  </div>
                ) : entityAccounts.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4">No accounts uploaded yet. Click "Upload COA" to get started.</p>
                ) : (
                  <div>
                    {/* Search filter */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative flex-1 max-w-sm">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search accounts by code or name..."
                          className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white"
                          value={accountSearch}
                          onChange={(e) => setAccountSearch(e.target.value)}
                        />
                      </div>
                      <span className="text-xs text-gray-400">
                        {(() => {
                          const filtered = entityAccounts.filter((ea) =>
                            !accountSearch ||
                            ea.local_code.toLowerCase().includes(accountSearch.toLowerCase()) ||
                            ea.local_name.toLowerCase().includes(accountSearch.toLowerCase()) ||
                            (ea.group_account_code || '').toLowerCase().includes(accountSearch.toLowerCase()) ||
                            (ea.group_account_name || '').toLowerCase().includes(accountSearch.toLowerCase())
                          );
                          return accountSearch
                            ? `${filtered.length} of ${entityAccounts.length} accounts`
                            : `${entityAccounts.length} accounts`;
                        })()}
                      </span>
                    </div>
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 z-10">
                        <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-200">
                          <th className="py-2 pr-4 pl-2">Code</th>
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Group Account</th>
                          <th className="py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {entityAccounts.filter((ea) =>
                          !accountSearch ||
                          ea.local_code.toLowerCase().includes(accountSearch.toLowerCase()) ||
                          ea.local_name.toLowerCase().includes(accountSearch.toLowerCase()) ||
                          (ea.group_account_code || '').toLowerCase().includes(accountSearch.toLowerCase()) ||
                          (ea.group_account_name || '').toLowerCase().includes(accountSearch.toLowerCase())
                        ).map((ea) => (
                          <tr key={ea.id} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 pr-4 font-mono text-xs text-gray-600">{ea.local_code}</td>
                            <td className="py-2 pr-4 text-gray-900">{ea.local_name}</td>
                            <td className="py-2 pr-4">
                              <span className={clsx(
                                'badge text-xs',
                                ea.mapping_status === 'mapped' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700',
                              )}>
                                {ea.mapping_status}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-gray-500 text-xs relative">
                              {editingAccountId === String(ea.id) ? (
                                <div className="relative">
                                  <div className="flex items-center gap-1">
                                    <div className="relative flex-1">
                                      <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                                      <input
                                        ref={gaSearchRef}
                                        type="text"
                                        placeholder="Search group account..."
                                        className="w-full pl-7 pr-2 py-1.5 text-xs border border-primary-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white"
                                        value={gaSearchQuery}
                                        onChange={(e) => setGaSearchQuery(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Escape') cancelEditing(); }}
                                      />
                                    </div>
                                    <button
                                      onClick={cancelEditing}
                                      className="p-1 hover:bg-gray-200 rounded text-gray-400"
                                      title="Cancel"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  {/* Dropdown */}
                                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                                    {savingMapping ? (
                                      <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-500">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Saving...
                                      </div>
                                    ) : filteredGroupAccounts.length === 0 ? (
                                      <div className="px-3 py-3 text-xs text-gray-400">No group accounts found</div>
                                    ) : (
                                      filteredGroupAccounts.slice(0, 30).map((ga) => (
                                        <button
                                          key={ga.id}
                                          onClick={() => saveMapping(String(ea.id), String(ga.id))}
                                          className={clsx(
                                            'w-full text-left px-3 py-2 text-xs hover:bg-primary-50 transition-colors flex items-center gap-2',
                                            String(ea.group_account_id) === String(ga.id) && 'bg-primary-50 font-medium',
                                          )}
                                        >
                                          <span className="font-mono text-gray-500 shrink-0">{ga.code}</span>
                                          <span className="text-gray-800 truncate">{ga.name}</span>
                                          {String(ea.group_account_id) === String(ga.id) && (
                                            <Check className="w-3 h-3 text-primary-600 ml-auto shrink-0" />
                                          )}
                                        </button>
                                      ))
                                    )}
                                    {filteredGroupAccounts.length > 30 && (
                                      <div className="px-3 py-1.5 text-xs text-gray-400 border-t border-gray-100">
                                        {filteredGroupAccounts.length - 30} more — refine search
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                ea.group_account_code
                                  ? `${ea.group_account_code} — ${ea.group_account_name}`
                                  : '—'
                              )}
                            </td>
                            <td className="py-2 w-10">
                              {editingAccountId !== String(ea.id) && (
                                <button
                                  onClick={() => startEditing(String(ea.id))}
                                  className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-primary-600 transition-colors"
                                  title="Edit mapping"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="card px-6 py-12 text-center">
            <FileSpreadsheet className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {searchQuery ? 'No entities match your search.' : 'No entities found.'}
            </p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {uploadEntityId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upload Entity COA</h2>
                {uploadEntity && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Entity: {uploadEntity.entity_name} ({uploadEntity.entity_code})
                  </p>
                )}
              </div>
              <button onClick={closeUploadModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Upload result */}
              {uploadResult && (
                <div className={clsx(
                  'px-4 py-3 rounded-lg border space-y-1',
                  uploadResult.errors.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200',
                )}>
                  <p className={clsx('text-sm font-medium', uploadResult.errors.length > 0 ? 'text-red-800' : 'text-green-800')}>
                    {uploadResult.errors.length > 0
                      ? uploadResult.errors[0]
                      : `${uploadResult.created} created · ${uploadResult.updated} updated · ${uploadResult.skipped} skipped`}
                  </p>
                  {uploadResult.ai_column_detection && (
                    <p className="text-xs text-green-700 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI detected columns: <span className="font-mono">{uploadResult.detected_code_column}</span> → code, <span className="font-mono">{uploadResult.detected_name_column}</span> → name
                    </p>
                  )}
                  {uploadResult.warnings.slice(0, 3).map((w, i) => (
                    <p key={i} className="text-xs text-amber-700">&bull; {w}</p>
                  ))}
                </div>
              )}

              {/* Hint */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium">AI-powered: Upload any COA format</p>
                  <p className="mt-0.5">
                    AI will automatically detect account code and name columns. Supports Excel (.xlsx, .xls) and CSV.{' '}
                    <button onClick={() => accountsApi.downloadCoaTemplate()} className="underline font-medium hover:text-blue-900">
                      Download sample template
                    </button>
                  </p>
                </div>
              </div>

              {/* Drop zone */}
              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setUploadFile(f); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setUploadFile(f); }}
                />
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <File className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{uploadFile.name}</p>
                      <p className="text-xs text-gray-500">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setUploadFile(null); }} className="p-1 hover:bg-gray-100 rounded">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop your COA file, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Any Excel or CSV format — AI will detect columns</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={closeUploadModal} className="btn-secondary">Close</button>
              <button onClick={handleUpload} disabled={uploading || !uploadFile} className="btn-primary">
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload COA</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Mapping Upload Modal */}
      {showBulkMapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Bulk Upload Mapping</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Upload a multi-sheet mapping file (e.g., Mappings.xlsx from EPM/FCCS). Entities are auto-matched via the Location Details sheet.
                </p>
              </div>
              <button onClick={closeBulkMappingModal} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              {/* Result */}
              {bulkMappingResult && (
                <div className="rounded-xl border border-green-200 bg-green-50 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-semibold text-green-800">
                      {bulkMappingResult.total_mapped} accounts mapped across {bulkMappingResult.entities_processed} entities
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    {bulkMappingResult.entity_results.map((r) => (
                      <div key={r.entity_code} className="flex items-center justify-between text-xs px-2 py-1.5 bg-white rounded-lg">
                        <span className="font-medium text-gray-900">
                          <span className="font-mono text-gray-400 mr-1.5">{r.entity_code}</span>
                          {r.entity_name}
                        </span>
                        <span className="flex items-center gap-3">
                          {r.status === 'no_sheet' ? (
                            <span className="text-amber-600">No sheet found</span>
                          ) : (
                            <>
                              <span className="text-green-600">{r.explicit_mapped + r.wildcard_mapped} mapped</span>
                              {r.skipped > 0 && <span className="text-gray-400">{r.skipped} skipped</span>}
                              <span className="text-gray-300">{r.sheet}</span>
                            </>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                  {bulkMappingResult.warnings.length > 0 && (
                    <div className="text-xs text-amber-700 mt-2">
                      {bulkMappingResult.warnings.slice(0, 10).map((w, i) => (
                        <p key={i}>{w}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* File drop zone */}
              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
                onClick={() => bulkFileInputRef.current?.click()}
              >
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setBulkMappingFile(f); }}
                />
                {bulkMappingFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <File className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{bulkMappingFile.name}</p>
                      <p className="text-xs text-gray-500">{(bulkMappingFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setBulkMappingFile(null); }} className="p-1 hover:bg-gray-100 rounded">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <FileSpreadsheet className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Drop your <span className="font-semibold">Mappings.xlsx</span>, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Multi-sheet Excel with Location Details + per-entity mapping sheets</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={closeBulkMappingModal} className="btn-secondary">Close</button>
              <button onClick={handleBulkMappingUpload} disabled={bulkMappingUploading || !bulkMappingFile} className="btn-primary">
                {bulkMappingUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload Mapping</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
